import { useCallback, useMemo, useEffect, useState, useContext } from 'react';
import { Flex } from 'antd';
import { RcFile } from 'antd/es/upload';
import { useFormik } from 'formik';

import { useSubmitHrmRequestMutation } from '@Store/api/basicHrmApiSlice';
import { usePostOverseasFormMutation } from '@Features/EmployeeSelfService/requests/OverseasMission/store/api/overseasRequestApiSlice';
import { useFormUploadMutation } from '@Store/api/dmsApiSlice';
import { overseasMissionCreateRequisitionInfo } from '@Features/EmployeeSelfService/requests/OverseasMission/store/selectors/overseasMissionSelector';
import {
  useAppDispatch,
  useAppSelector,
  useNavigationToListRequests,
} from '@Hooks/index';
import { currentUserSelector } from '@Store/selectors/userInfoSelector';
import {
  getCountriesDictSelector,
  getCompaniesDictSelector,
  getOverseasSponsorsDictSelector,
} from '@Store/selectors/dictionarySelector';
import { getTotalDays } from '@Utils/transformDateTime';
import {
  transformDictToOptions,
  transformCountriesDictToOptions,
  transformCompaniesDictToOptions,
} from '@Utils/transformToOptions';
import FormItems from '@Components/MainForm/components/FormItems/FormItems';
import { MainForm } from '@Components/index';
import { RcCustomRequestOptions } from '@Components/types/UploadType';
import {
  DocumentFormUploadResponse,
  OverseasMissionFormPostRequest,
  RequestError,
} from '@Types/api/form';
import { validateForm } from '@Features/utils/validateForm';
import { overseasMissionActions } from '@Features/EmployeeSelfService/requests/OverseasMission/store/overseasMissionSlice';
import { generateFilepath } from '@Utils/generateFilepath';
import { fileUploadActions } from '@Store/slices/fileUploadSlice';
import { currentFileUploadSelector } from '@Store/selectors/fileUploadSelector';
import { NotificationContext } from '@Context/NotificationContext';
import { FooterActions } from '@Enum/index';

import OverseasDetailsSection from '../components/overseasDetailSection';
import { OverseasMissionFormValues } from '../types';
import { initialValuesForm } from '../initialValuesForm';
import { OverseasMissionFormSchema } from '../validationSchema';
import { getFormUploadFileMetaData } from '../utils/document';
import { mapSponsoredByExtraField } from '../utils/mapSponsoredByExtraField';

const OverseasMissionCreateForm: React.FC = () => {
  const {
    saveSuccessNotification,
    errorNotification,
    submitSuccessNotification,
  } = useContext(NotificationContext);
  const countriesDict = useAppSelector(getCountriesDictSelector);
  const companiesDict = useAppSelector(getCompaniesDictSelector);
  const currentFileUpload = useAppSelector(currentFileUploadSelector);
  const overseasSponsorsDict = useAppSelector(getOverseasSponsorsDictSelector);
  const requisitionInfo = useAppSelector(overseasMissionCreateRequisitionInfo);
  const [postOverseasForm] = usePostOverseasFormMutation();
  const [submitForm] = useSubmitHrmRequestMutation();
  const [formUpload] = useFormUploadMutation();
  const currentUser = useAppSelector(currentUserSelector);
  const dispatch = useAppDispatch();
  const [disabledSaveForm, setDisableSaveForm] = useState(false);
  const { handleBackToListRequests } = useNavigationToListRequests();

  const countriesOptions = useMemo(
    () => transformCountriesDictToOptions(countriesDict),
    [countriesDict]
  );
  const companiesOptions = useMemo(
    () => transformCompaniesDictToOptions(companiesDict),
    [companiesDict]
  );
  const sponsorsOptions = useMemo(
    () => transformDictToOptions(overseasSponsorsDict),
    [overseasSponsorsDict]
  );

  useEffect(
    () => () => {
      dispatch(overseasMissionActions.setOverseasMission(null));
      dispatch(fileUploadActions.setFile(null));
    },
    []
  );

  const uploadFile = useCallback(
    (reqFileId: string): Promise<DocumentFormUploadResponse> | null => {
      const { formData, filepath } = currentFileUpload;

      if (!filepath) {
        return null;
      }

      const fileMetaData = getFormUploadFileMetaData(reqFileId, filepath);

      return formUpload({
        document: JSON.stringify(fileMetaData),
        body: formData,
      }).unwrap();
    },
    [currentFileUpload]
  );

  const handleSubmitForm = useCallback(
    (values: OverseasMissionFormValues): void => {
      const submitFormHandler = async (): Promise<void> => {
        const sponsoredByExtraMapped = mapSponsoredByExtraField(
          values.sponsoredBy as string,
          values.sponsoredByExtra,
          companiesOptions
        );

        let body = {
          empNumber: values.employeeNumber as string,
          currentLocation: values.currentLocation,
          destination: values.placeOfTravel,
          travelDate: values.dateOfTravel,
          returnDate: values.returnDate,
          totDays: values.totDays,
          remarks: values.purposeOfTravel,
          eligibleDays: values.approvedTravelAmount as number,
          leaveTypeID: values.country as string,
          ticketRequired: values.sponsoredBy as string,
          filepath: currentFileUpload.filepath || undefined,
          ...sponsoredByExtraMapped,
        } as OverseasMissionFormPostRequest;

        if (requisitionInfo?.requisitionNo) {
          body = {
            ...body,
            requestId: Number(requisitionInfo?.requisitionNo),
          };
        }

        setDisableSaveForm(true);

        try {
          const {
            createdDate,
            reqStatusId,
            requestId: reqId,
          } = await postOverseasForm(body).unwrap();

          dispatch(
            overseasMissionActions.setOverseasMission({
              createdDate,
              requestId: reqId,
              reqStatusId,
            })
          );

          await uploadFile(String(reqId));
          await submitForm(String(reqId));

          submitSuccessNotification(String(reqId));

          setDisableSaveForm(false);

          handleBackToListRequests();
        } catch (error) {
          const message = (error as RequestError)?.data;
          errorNotification(message);

          setDisableSaveForm(false);
        }
      };

      submitFormHandler();
    },
    [
      companiesOptions,
      requisitionInfo,
      handleBackToListRequests,
      currentFileUpload,
      uploadFile,
    ]
  );

  const formik = useFormik({
    initialValues: initialValuesForm,
    enableReinitialize: true,
    validationSchema: OverseasMissionFormSchema,
    onSubmit: handleSubmitForm,
  });

  const handleSaveAsDraft = useCallback((): void => {
    const saveAsDraft = async (): Promise<void> => {
      const sponsoredByExtraMapped = mapSponsoredByExtraField(
        formik.values.sponsoredBy as string,
        formik.values.sponsoredByExtra,
        companiesOptions
      );
      const body = {
        empNumber: formik.values.employeeNumber as string,
        currentLocation: formik.values.currentLocation,
        destination: formik.values.placeOfTravel,
        travelDate: formik.values.dateOfTravel,
        returnDate: formik.values.returnDate,
        totDays: formik.values.totDays,
        remarks: formik.values.purposeOfTravel,
        eligibleDays: formik.values.approvedTravelAmount as number,
        leaveTypeID: formik.values.country as string,
        ticketRequired: formik.values.sponsoredBy as string,
        filepath: currentFileUpload.filepath || undefined,
        ...sponsoredByExtraMapped,
      };

      setDisableSaveForm(true);

      try {
        const {
          requestId: reqId,
          createdDate,
          reqStatusId,
        } = await postOverseasForm(body).unwrap();

        dispatch(
          overseasMissionActions.setOverseasMission({
            createdDate,
            requestId: reqId,
            reqStatusId,
          })
        );

        await uploadFile(String(reqId));

        saveSuccessNotification(String(reqId));

        setDisableSaveForm(false);

        handleBackToListRequests();
      } catch (error) {
        const message = (error as RequestError)?.data;
        errorNotification(message);

        setDisableSaveForm(false);
      }
    };

    const saveAsDraftHandler = async (): Promise<void> => {
      const isFormValid = await validateForm(OverseasMissionFormSchema, formik);

      if (!isFormValid) {
        return;
      }

      await saveAsDraft();
    };

    saveAsDraftHandler();
  }, [
    companiesOptions,
    formik,
    currentFileUpload,
    handleBackToListRequests,
    uploadFile,
  ]);

  useEffect(() => {
    const { empNumber, empName, occupation, currentLocation } = currentUser;

    formik.setFieldValue('employeeNumber', empNumber);
    formik.setFieldValue('employeeName', empName);
    formik.setFieldValue('designation', occupation);
    formik.setFieldValue('currentLocation', currentLocation);
  }, [currentUser]);

  const handleAttachmentRemove = useCallback((): void => {
    dispatch(fileUploadActions.setFile(null));
  }, []);

  const handleAttachmentRequest = useCallback(
    (options: RcCustomRequestOptions): void => {
      const { file, onSuccess, onError } = options;
      const cb = (): void => {};
      const successCb = onSuccess || cb;
      const errorCb = onError || cb;
      const formData = new FormData();
      formData.append('file', file);

      if (file) {
        const filepath = generateFilepath('Overseas', (file as RcFile).name);
        dispatch(
          fileUploadActions.setFile({
            uploadedFile: file as RcFile,
            formData,
            filepath,
          })
        );
        successCb('Ok');
      } else {
        errorCb(new Error('file not uploaded'));
      }
    },
    []
  );

  useEffect(() => {
    if (formik.values.dateOfTravel && formik.values.returnDate) {
      const totalDays = getTotalDays(
        formik.values.returnDate,
        formik.values.dateOfTravel
      );

      formik.setFieldValue('totDays', totalDays);
    } else {
      formik.setFieldValue('totDays', '');
    }
  }, [formik.values.dateOfTravel, formik.values.returnDate]);

  const handleFooterAction = useCallback(
    (action: FooterActions) => {
      if (action === FooterActions.SAVE_AS_DRAFT) {
        handleSaveAsDraft();
      }

      if (action === FooterActions.BACK_TO_REQUESTS) {
        handleBackToListRequests();
      }
    },
    [handleBackToListRequests, handleSaveAsDraft]
  );

  return (
    <Flex gap={10} vertical style={{ height: '100%' }}>
      <MainForm
        handleSubmit={formik.handleSubmit}
        userInfo={currentUser}
        requisitionInfo={requisitionInfo}
        disabledSaveForm={disabledSaveForm}
        onFooterAction={handleFooterAction}
        formType="create"
      >
        <FormItems>
          <OverseasDetailsSection
            label="Overseas Mission Details"
            formik={formik}
            leaveTypeOptions={countriesOptions}
            onFileRequest={handleAttachmentRequest}
            onFileRemove={handleAttachmentRemove}
            sponsorOptions={sponsorsOptions}
            companyOptions={companiesOptions}
          />
        </FormItems>
      </MainForm>
    </Flex>
  );
};

export default OverseasMissionCreateForm;
